import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import { api } from '../services/api';
import { toast } from '../contexts/ToastContext';

interface ActiveOtp {
  id: number;
  userId: number;
  code: string;
  sessionId: string;
  expiresAt: string;
  createdAt: string;
  userName: string | null;
  userEmail: string | null;
}

interface UserRow {
  id: number;
  name: string | null;
  email: string;
  twoFactorEnabled: boolean;
  status: string;
}

export default function TwoFactorManagementPage() {
  const qc = useQueryClient();
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [userLinks, setUserLinks] = useState<Record<number, string>>({});
  const [linkLoading, setLinkLoading] = useState<number | null>(null);
  const [copiedLink, setCopiedLink] = useState<number | null>(null);

  const otpsQuery = useQuery({
    queryKey: ['otp-active'],
    queryFn: async () => {
      const res = await api.get('/otp/active');
      return (res?.data ?? []) as ActiveOtp[];
    },
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const usersQuery = useQuery({
    queryKey: ['users-2fa'],
    queryFn: async () => {
      const res = await api.get('/otp/users');
      return (res?.data?.items ?? res?.data ?? []) as UserRow[];
    },
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ userId, enabled }: { userId: number; enabled: boolean }) => {
      return api.post(`/otp/toggle/${userId}`, { enabled });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users-2fa'] });
      qc.invalidateQueries({ queryKey: ['otp-active'] });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update 2FA setting');
    },
  });

  const copyCode = (otp: ActiveOtp) => {
    navigator.clipboard.writeText(otp.code);
    setCopiedId(otp.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getSetupLink = async (userId: number) => {
    setLinkLoading(userId);
    try {
      const res = await api.post(`/otp/user-link/${userId}`);
      const token = (res as any)?.data?.token;
      const link = `${window.location.origin}/reset-password?token=${token}`;
      setUserLinks(prev => ({ ...prev, [userId]: link }));
    } catch (error: any) {
      toast.error(error?.message || 'Failed to generate setup link');
    } finally {
      setLinkLoading(null);
    }
  };

  const copyLink = (userId: number) => {
    const link = userLinks[userId];
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopiedLink(userId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const fmtExpiry = (d: string) => {
    try {
      const diff = Math.round((new Date(d).getTime() - Date.now()) / 1000);
      if (diff <= 0) return 'Expired';
      if (diff < 60) return `${diff}s left`;
      return `${Math.round(diff / 60)}m left`;
    } catch { return d; }
  };

  // Shared by the desktop tables and the mobile card lists
  const otpColumns: TableColumn[] = [
    { key: 'userName', label: 'User', render: (value) => value ?? '—' },
    { key: 'userEmail', label: 'Email', render: (value) => value ?? '—' },
    {
      key: 'code',
      label: 'OTP Code',
      render: (value, otp) => (
        <div className="flex items-center gap-2 justify-end flex-wrap">
          <span className="font-mono text-lg font-bold tracking-[0.3em] text-brand-700 bg-brand-50 px-3 py-1 rounded-lg">
            {value}
          </span>
          <button
            onClick={() => copyCode(otp)}
            className="text-xs text-gray-500 border border-gray-200 rounded px-2 py-1 hover:bg-gray-100 transition-colors"
          >
            {copiedId === otp.id ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      ),
    },
    {
      key: 'expiresAt',
      label: 'Expires',
      render: (value) => (
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
          fmtExpiry(value) === 'Expired' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
        }`}>
          {fmtExpiry(value)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Generated At',
      render: (value) => new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }),
    },
  ];

  const userColumns: TableColumn[] = [
    { key: 'name', label: 'Name', render: (value) => value ?? '—' },
    { key: 'email', label: 'Email' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          value === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>{value}</span>
      ),
    },
    {
      key: 'twoFactorEnabled',
      label: '2FA Status',
      render: (value) => (
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          value ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {value ? '🔒 Enabled' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'twoFactorAction',
      label: '2FA Action',
      render: (_, u) => (
        <button
          onClick={() => toggleMutation.mutate({ userId: u.id, enabled: !u.twoFactorEnabled })}
          disabled={toggleMutation.isPending}
          className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors disabled:opacity-50 ${
            u.twoFactorEnabled
              ? 'border-red-300 text-red-600 hover:bg-red-50'
              : 'border-brand-300 text-brand-600 hover:bg-brand-50'
          }`}
        >
          {u.twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
        </button>
      ),
    },
    {
      key: 'setupLink',
      label: 'Setup Link',
      render: (_, u) => (
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={() => getSetupLink(u.id)}
            disabled={linkLoading === u.id}
            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {linkLoading === u.id ? 'Generating...' : 'Get Link'}
          </button>
          {userLinks[u.id] && (
            <div className="flex items-center gap-2 max-w-full">
              <code className="text-xs bg-white border border-brand-200 rounded px-2 py-1 text-brand-900 truncate font-mono max-w-[140px]">
                {userLinks[u.id]}
              </code>
              <button
                onClick={() => copyLink(u.id)}
                className="shrink-0 text-xs px-3 py-1 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-medium"
              >
                {copiedLink === u.id ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100">

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300">
            <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow-md">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">2FA Management</h1>
              <p className="text-gray-600 mt-0.5">Enable or disable two-factor authentication per user and monitor active verification sessions</p>
            </div>
          </div>
        </div>

        {/* Active OTPs — testing panel */}
        <div className="bg-white rounded-xl shadow border border-gray-200 mb-6 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <h2 className="text-sm font-semibold text-gray-900">Active Verification Sessions</h2>
            </div>
            <button
              onClick={() => qc.invalidateQueries({ queryKey: ['otp-active'] })}
              className="text-xs text-gray-500 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-100 flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {otpsQuery.isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading active OTPs...</div>
          ) : !otpsQuery.data?.length ? (
            <div className="p-8 text-center">
              <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-400 text-sm">No active sessions. Codes appear here when a 2FA-enabled user initiates login.</p>
            </div>
          ) : (
            <>
            <div className="p-3 lg:hidden">
              <MobileCardList columns={otpColumns} data={otpsQuery.data} />
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['User', 'Email', 'OTP Code', 'Expires', 'Generated At'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {otpsQuery.data.map(otp => (
                    <tr key={otp.id} className="hover:bg-brand-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{otp.userName ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{otp.userEmail ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-lg font-bold tracking-[0.3em] text-brand-700 bg-brand-50 px-3 py-1 rounded-lg">
                            {otp.code}
                          </span>
                          <button
                            onClick={() => copyCode(otp)}
                            className="text-xs text-gray-500 border border-gray-200 rounded px-2 py-1 hover:bg-gray-100 transition-colors"
                          >
                            {copiedId === otp.id ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          fmtExpiry(otp.expiresAt) === 'Expired'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {fmtExpiry(otp.expiresAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(otp.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Users — enable/disable 2FA */}
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-sm font-semibold text-gray-900">User 2FA Settings</h2>
            <span className="text-xs text-gray-500">({usersQuery.data?.length ?? 0} users)</span>
          </div>

          {usersQuery.isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading users...</div>
          ) : (
            <>
            <div className="p-3 lg:hidden">
              <MobileCardList columns={userColumns} data={usersQuery.data ?? []} emptyMessage="No users found" />
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Name', 'Email', 'Status', '2FA Status', '2FA Action', 'Setup Link'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(usersQuery.data ?? []).map(user => (
                    <React.Fragment key={user.id}>
                      <tr className="hover:bg-brand-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{user.name ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>{user.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            user.twoFactorEnabled ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {user.twoFactorEnabled ? '🔒 Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleMutation.mutate({ userId: user.id, enabled: !user.twoFactorEnabled })}
                            disabled={toggleMutation.isPending}
                            className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors disabled:opacity-50 ${
                              user.twoFactorEnabled
                                ? 'border-red-300 text-red-600 hover:bg-red-50'
                                : 'border-brand-300 text-brand-600 hover:bg-brand-50'
                            }`}
                          >
                            {user.twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => getSetupLink(user.id)}
                            disabled={linkLoading === user.id}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center gap-1"
                          >
                            {linkLoading === user.id ? (
                              <>
                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                </svg>
                                Generating...
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                Get Link
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {userLinks[user.id] && (
                        <tr className="bg-brand-50 border-t border-brand-100">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-brand-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              <span className="text-xs text-brand-700 font-medium mr-1">Setup link for {user.name ?? user.email}:</span>
                              <code className="flex-1 text-xs bg-white border border-brand-200 rounded px-2 py-1 text-brand-900 truncate font-mono">
                                {userLinks[user.id]}
                              </code>
                              <button
                                onClick={() => copyLink(user.id)}
                                className="shrink-0 text-xs px-3 py-1 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-medium"
                              >
                                {copiedLink === user.id ? '✓ Copied' : 'Copy'}
                              </button>
                              <button
                                onClick={() => setUserLinks(prev => { const n = { ...prev }; delete n[user.id]; return n; })}
                                className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Dismiss"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

      </div>
    </Layout>
  );
}
